import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { toast } from 'sonner';

import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { LanguageSwitcher } from '@/components/language-switcher';
import { useTranslation } from 'react-i18next';
import { useProvider } from '@/hooks/useTenant';
import { useBrandLogo } from '@/hooks/useBrandLogo';
import { usePasswordLoginAvailability } from '@/hooks/auth/usePasswordLoginAvailability';
import { authService } from '@/services/auth.service';
import { getCurrentDomain } from '@/utils/domain';
import { resolvePasswordValidationMessage } from '@/lib/auth/password-validation';
import { formatPasswordPolicyRules } from '@/lib/auth/password-policy';
import {
  extractErrorStatus,
  extractJsonApiErrorCode,
  extractJsonApiErrorMessage,
  extractJsonApiFieldErrors,
} from '@/lib/jsonapi/errors';

type FormValues = {
  email: string;
  password: string;
  passwordConfirmation: string;
};

export default function ProviderResetPassword() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const provider = useProvider();
  const brandLogo = useBrandLogo();
  const brandName = provider?.name ?? provider?.slug ?? t('navigation.providerAccount');

  const params = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const emailFromQuery = params.get('email') ?? '';
  const token = params.get('token') ?? '';
  const scope = params.get('scope');

  useEffect(() => {
    if (scope === 'customer') {
      navigate(`/customer/reset-password${location.search}`, { replace: true });
    }
  }, [location.search, navigate, scope]);

  const domainInfo = useMemo(() => getCurrentDomain(), []);
  const resolvedTenantSlug = provider?.slug ?? domainInfo.subdomain ?? undefined;
  const { enabled: passwordLoginEnabled, isLoading: isAvailabilityLoading, policy } =
    usePasswordLoginAvailability('provider', {
      tenantSlug: resolvedTenantSlug,
    });
  const passwordRules = useMemo(() => formatPasswordPolicyRules(t, policy), [policy, t]);

  const schema = useMemo(
    () =>
      z
        .object({
          email: z.string().email(t('auth.invalidEmail')),
          password: z.string().min(8, t('auth.passwordTooShort')),
          passwordConfirmation: z.string(),
        })
        .refine((values) => values.password === values.passwordConfirmation, {
          message: t('auth.resetPassword.passwordMismatch'),
          path: ['passwordConfirmation'],
        }),
    [t],
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      email: emailFromQuery,
      password: '',
      passwordConfirmation: '',
    },
  });

  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const mutation = useMutation({
    mutationFn: (values: FormValues) =>
      authService.resetProviderPassword({
        email: values.email,
        token,
        password: values.password,
        passwordConfirmation: values.passwordConfirmation,
      }),
    onSuccess: (response) => {
      setErrorMessage(null);
      const message = response.message || t('auth.resetPassword.success');
      toast.success(message);
      navigate('/provider/login', { replace: true });
      form.clearErrors();
    },
    onError: (error: unknown) => {
      const status = extractErrorStatus(error);
      const code = extractJsonApiErrorCode(error);
      const fieldErrors = extractJsonApiFieldErrors(error);

      Object.entries(fieldErrors).forEach(([field, message]) => {
        const resolvedMessage = resolvePasswordValidationMessage(t, message) ?? message;
        if (field === 'email') {
          form.setError('email', { message: resolvedMessage });
        }
        if (field === 'password') {
          form.setError('password', { message: resolvedMessage });
        }
        if (field === 'passwordConfirmation') {
          form.setError('passwordConfirmation', { message: resolvedMessage });
        }
      });

      const rawMessage = extractJsonApiErrorMessage(error, t('auth.resetPassword.error'));
      let message = resolvePasswordValidationMessage(t, rawMessage) ?? rawMessage;
      if (code === 'PASSWORD_RESET_INVALID') {
        message = t('auth.resetPassword.invalidToken');
      }
      if (status === 423 || code === 'PASSWORD_LOGIN_DISABLED') {
        message = t('auth.passwordLoginDisabled');
      }
      setErrorMessage(message);
    },
  });

  const tokenMissing = token.trim().length === 0;

  const handleSubmit = (values: FormValues) => {
    setErrorMessage(null);

    if (tokenMissing) {
      setErrorMessage(t('auth.resetPassword.invalidLink'));
      return;
    }

    mutation.mutate(values);
  };

  const isFormDisabled =
    mutation.isPending || isAvailabilityLoading || !passwordLoginEnabled || tokenMissing;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4 relative">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>

      <div className="w-full max-w-md space-y-6">
        <div className="flex flex-col items-center space-y-1">
          {brandLogo ? (
            <img
              src={brandLogo}
              alt={`${brandName} logo`}
              className="h-10 w-auto max-w-[200px] object-contain"
            />
          ) : (
            <span className="text-2xl font-semibold text-foreground">{brandName}</span>
          )}
        </div>

        <Card className="w-full">
          <CardHeader className="space-y-1">
            <CardTitle className="text-2xl">{t('auth.resetPassword.title')}</CardTitle>
            <CardDescription>{t('auth.resetPassword.description')}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!passwordLoginEnabled && !isAvailabilityLoading ? (
              <Alert variant="destructive">
                <AlertDescription>{t('auth.passwordLoginDisabled')}</AlertDescription>
              </Alert>
            ) : null}

            {tokenMissing ? (
              <Alert variant="destructive">
                <AlertDescription>{t('auth.resetPassword.invalidLink')}</AlertDescription>
              </Alert>
            ) : null}

            {errorMessage ? (
              <Alert variant="destructive">
                <AlertDescription>{errorMessage}</AlertDescription>
              </Alert>
            ) : null}

            <Form {...form}>
              <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.email')}</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          autoComplete="email"
                          placeholder={t('auth.emailPlaceholder')}
                          disabled={isFormDisabled}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.resetPassword.passwordLabel')}</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="new-password"
                          placeholder={t('auth.passwordPlaceholder')}
                          disabled={isFormDisabled}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="passwordConfirmation"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('auth.resetPassword.confirmPasswordLabel')}</FormLabel>
                      <FormControl>
                        <Input
                          type="password"
                          autoComplete="new-password"
                          placeholder={t('auth.resetPassword.confirmPasswordPlaceholder')}
                          disabled={isFormDisabled}
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {passwordRules.length > 0 ? (
                  <div className="rounded-md border border-muted bg-muted/10 px-3 py-2 text-xs text-muted-foreground">
                    <p className="mb-1 text-foreground">
                      {t('auth.passwordPolicy.title', 'Password requirements')}
                    </p>
                    <ul className="list-disc space-y-1 pl-4">
                      {passwordRules.map((rule) => (
                        <li key={rule}>{rule}</li>
                      ))}
                    </ul>
                  </div>
                ) : null}

                <Button type="submit" className="w-full" disabled={isFormDisabled}>
                  {mutation.isPending
                    ? t('auth.resetPassword.submitting')
                    : t('auth.resetPassword.submit')}
                </Button>
              </form>
            </Form>
          </CardContent>
          <CardFooter>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => navigate('/provider/login')}
            >
              {t('auth.resetPassword.backToLogin')}
            </Button>
          </CardFooter>
        </Card>
      </div>
    </div>
  );
}
